import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { scheduleApi } from '../lib/api';

const LS_KEY = 'bil_schedule_events_v1';

function readLocal() {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(LS_KEY) : null;
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}
function writeLocal(items) {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LS_KEY, JSON.stringify(items));
    }
  } catch (_) { /* quota / private mode — ignore */ }
}
function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `g_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function filterByScope(items, scope) {
  const now = new Date().toISOString();
  let out = items;
  if (scope === 'upcoming') {
    out = items.filter((e) => e.start_at >= now && e.status !== 'cancelled');
    out.sort((a, b) => a.start_at.localeCompare(b.start_at));
  } else if (scope === 'past') {
    out = items.filter((e) => e.start_at < now);
    out.sort((a, b) => b.start_at.localeCompare(a.start_at));
  } else {
    out = [...items].sort((a, b) => a.start_at.localeCompare(b.start_at));
  }
  return out;
}

/**
 * Storage abstraction for Schedule & Remind.
 * - Signed-in users → backend (/api/schedule/events)
 * - Guests          → browser localStorage (free, no signup required)
 *
 * Exposes the same shape regardless of mode so the UI doesn't need to branch.
 */
export default function useScheduleStore(scope = 'upcoming') {
  const { user, loading: authLoading } = useAuth();
  const isGuest = !authLoading && (!user || user === false);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isGuest) {
        setItems(filterByScope(readLocal(), scope));
      } else if (user) {
        const { data } = await scheduleApi.list(scope);
        setItems(data || []);
      }
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [isGuest, user, scope]);

  useEffect(() => {
    if (authLoading) return;
    load();
  }, [authLoading, load]);

  const create = useCallback(async (payload) => {
    if (isGuest) {
      const all = readLocal();
      const doc = {
        id: uuid(),
        title: (payload.title || 'Appointment').trim(),
        start_at: payload.start_at,
        location: payload.location || '',
        notes: payload.notes || '',
        remind_minutes_before: payload.remind_minutes_before ?? 60,
        color: payload.color || '#002677',
        status: 'scheduled',
        reminded: false,
        reminded_24h: false,
        reminded_1h: false,
        customer_name: payload.customer_name || '',
        customer_email: payload.customer_email || '',
        customer_phone: payload.customer_phone || '',
        duration_minutes: payload.duration_minutes || 30,
        channels: payload.channels || [],
        timezone: payload.timezone || null,
        attendees: Array.isArray(payload.attendees) ? payload.attendees : [],
        meeting_provider: payload.meeting_provider || null,
        meeting_link: payload.meeting_link || '',
        created_at: new Date().toISOString(),
        _guest: true,
      };
      writeLocal([...all, doc]);
      await load();
      return doc;
    }
    const { data } = await scheduleApi.create(payload);
    await load();
    return data;
  }, [isGuest, load]);

  const update = useCallback(async (id, patch) => {
    if (isGuest) {
      const all = readLocal();
      const next = all.map((e) => (e.id === id ? { ...e, ...patch, updated_at: new Date().toISOString() } : e));
      writeLocal(next);
      await load();
      return next.find((e) => e.id === id);
    }
    const { data } = await scheduleApi.update(id, patch);
    await load();
    return data;
  }, [isGuest, load]);

  const remove = useCallback(async (id) => {
    if (isGuest) {
      const all = readLocal();
      writeLocal(all.filter((e) => e.id !== id));
      await load();
      return;
    }
    await scheduleApi.delete(id);
    await load();
  }, [isGuest, load]);

  return { items, loading, isGuest, authLoading, reload: load, create, update, remove };
}

// Exposed so the global reminder hook can scan guest data too.
export { readLocal as readGuestEvents, writeLocal as writeGuestEvents, LS_KEY as GUEST_STORAGE_KEY };
